<template>
  <div class="spark-accepted-container">
    <div v-if="details && details.is_accepted">
      <!-- Acceptance Banner -->
      <div class="spark-accepted-banner">
        <div class="spark-accepted-banner__icon">
          <i class="fa-solid fa-check"></i>
        </div>
        <div class="spark-accepted-banner__text">
          <h6>Accepted & Confirmed</h6>
          <p>
            <i class="fa-regular fa-clock me-1"></i> {{ formattedAcceptedDate }}
            <span v-if="relativeAcceptedTime" class="ms-1 fw-semibold">({{ relativeAcceptedTime }})</span>
          </p>
        </div>
      </div>

      <!-- Acceptance KPIs -->
      <div class="spark-accepted-kpis">
        <div class="spark-accepted-kpi-card">
          <div class="spark-accepted-kpi-card__label">Option Accepted</div>
          <div class="spark-accepted-kpi-card__value" style="font-size: 0.9375rem; color: #00A0C1;">{{ acceptedOptionText }}</div>
        </div>
        <div class="spark-accepted-kpi-card">
          <div class="spark-accepted-kpi-card__label">Value Accepted</div>
          <div class="spark-accepted-kpi-card__value" style="color: #059669;">{{ details.total_accepted }}</div>
        </div>
      </div>

      <!-- Signer & Organisation Details Card -->
      <div class="spark-inspector-card mb-3">
        <div class="spark-inspector-card__header">
          <span class="spark-inspector-card__title">
            <i class="fa-solid fa-signature text-primary me-2"></i> Signer Information
          </span>
        </div>
        <div class="spark-inspector-card__body">
          <!-- Organisation -->
          <div class="spark-meta-field" v-if="details.organisation_name">
            <span class="spark-meta-field__label">Organisation</span>
            <div class="d-flex align-items-center justify-content-between">
              <router-link v-if="details.organisation_uuid"
                :to="{ name: 'clients-dashboard', query: { organisation_uuid: details.organisation_uuid } }"
                class="spark-meta-field__value fw-semibold text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                title="View Organisation">
                <span>{{ details.organisation_name }}</span>
                <i class="fa-solid fa-arrow-up-right-from-square small opacity-75"></i>
              </router-link>
              <span v-else class="spark-meta-field__value fw-semibold text-dark">
                {{ details.organisation_name }}
              </span>
              <button type="button" class="spark-btn-copy spark-btn-copy--sm" 
                @click="copyToClipboard(details.organisation_name, 'Organisation Name')" title="Copy Organisation">
                <i class="fa-regular fa-copy"></i>
              </button>
            </div>
            <span v-if="details.original_organisation_name && details.original_organisation_name !== details.organisation_name" 
              class="spark-meta-former-label mt-1">
              <i class="fa-solid fa-history me-1"></i> Formerly: {{ details.original_organisation_name }}
            </span>
          </div>

          <!-- Business Number / ABN -->
          <div class="spark-meta-field" v-if="details.business_number || details.abn">
            <span class="spark-meta-field__label">{{ businessNumberShortLabel }}</span>
            <div class="d-flex align-items-center justify-content-between">
              <span class="spark-meta-field__value font-monospace">
                {{ details.business_number || details.abn }}
              </span>
              <button type="button" class="spark-btn-copy spark-btn-copy--sm" 
                @click="copyToClipboard(details.business_number || details.abn, businessNumberShortLabel)" title="Copy Business Number">
                <i class="fa-regular fa-copy"></i>
              </button>
            </div>
            <span v-if="details.original_abn && details.original_abn !== (details.business_number || details.abn)" 
              class="spark-meta-former-label mt-1">
              <i class="fa-solid fa-history me-1"></i> Formerly: {{ details.original_abn }}
            </span>
          </div>

          <!-- Signer Email -->
          <div class="spark-meta-field">
            <span class="spark-meta-field__label">Accepted By (Signer)</span>
            <div class="spark-contact-pill">
              <div class="spark-contact-avatar">
                <i class="fa-solid fa-user-check"></i>
              </div>
              <div class="spark-contact-details">
                <span class="spark-contact-name">{{ signerName }}</span>
                <div class="d-flex align-items-center justify-content-between">
                  <a v-if="details.accepted_by_email" :href="'mailto:' + details.accepted_by_email" class="spark-contact-email" :title="'Email ' + details.accepted_by_email">
                    {{ details.accepted_by_email }}
                  </a>
                  <span v-else class="text-muted small">No email recorded</span>
                  <button v-if="details.accepted_by_email" type="button" class="spark-btn-copy spark-btn-copy--sm" 
                    @click="copyToClipboard(details.accepted_by_email, 'Signer Email')" title="Copy Email">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- IP Address & Verification Grid -->
          <div class="spark-meta-grid-2 pt-1 border-top">
            <div class="spark-meta-field mb-0">
              <span class="spark-meta-field__label">Signer IP Address</span>
              <div class="d-flex align-items-center justify-content-between">
                <span class="spark-meta-field__value font-monospace small" :title="details.accepted_by_ip">
                  <i class="fa-solid fa-network-wired me-1 text-muted"></i>
                  {{ formattedIp }}
                </span>
                <button v-if="details.accepted_by_ip" type="button" class="spark-btn-copy spark-btn-copy--sm" 
                  @click="copyToClipboard(details.accepted_by_ip, 'IP Address')" title="Copy IP">
                  <i class="fa-regular fa-copy"></i>
                </button>
              </div>
            </div>

            <div class="spark-meta-field mb-0">
              <span class="spark-meta-field__label">Security Status</span>
              <span class="spark-meta-field__value small text-success fw-semibold">
                <i class="fa-solid fa-shield-halved me-1"></i> Audit Logged
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Products & Services Accepted Card -->
      <div class="spark-inspector-card mb-3">
        <div class="spark-inspector-card__header">
          <span class="spark-inspector-card__title">
            <i class="fa-solid fa-list-check text-primary me-2"></i> Accepted Products & Services
          </span>
          <span class="spark-pill-badge spark-pill-badge--primary" v-if="details.accepted_items">
            {{ details.accepted_items.length }} {{ details.accepted_items.length === 1 ? 'item' : 'items' }}
          </span>
        </div>
        <div class="spark-inspector-card__body p-0">
          <div v-if="details.accepted_items && details.accepted_items.length > 0" class="spark-accepted-items-list-modern">
            <div v-for="item in details.accepted_items" :key="item.id" class="spark-accepted-item-row-modern">
              <div class="spark-accepted-item-row-modern__info">
                <span class="spark-accepted-item-row-modern__name">
                  {{ item.Product ? item.Product.display_name : (item.display_name || 'Item') }}
                </span>
                <span class="spark-accepted-item-row-modern__qty badge bg-light text-secondary border">
                  {{ item.quantity }}x
                </span>
              </div>
              <div class="spark-accepted-item-row-modern__price">
                {{ formatCurrency((item.price - item.discount) * item.quantity) }}
              </div>
            </div>
          </div>
          <div v-else class="text-muted p-3 text-center" style="font-size: 0.8125rem;">
            No specific items recorded.
          </div>
        </div>
      </div>

    </div>
    <div v-else class="text-center p-4" style="background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px;">
      <i class="fa-regular fa-circle-question text-muted mb-2" style="font-size: 1.5rem;"></i>
      <p class="text-muted mb-0" style="font-size: 0.875rem;">This contract has not been accepted yet.</p>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { toOptionName } from '@/plugins/helper.js';
import { getBusinessNumberShortLabel } from '@/plugins/businessValidator.js';

export default {
  name: 'ContractAcceptedDetails',
  props: {
    details: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    formatCurrency(value) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) {
        return '$0.00';
      }
      // Adds GST (10%) to the line item total for display
      return `$${(numericValue * 1.1).toFixed(2)}`;
    },
    async copyToClipboard(text, label = "Item") {
      if (!text || text === 'N/A' || text === 'None' || text === 'No Business Number') return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        }
        if (this.$toast) {
          this.$toast.success(`${label} copied to clipboard!`);
        }
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    }
  },
  computed: {
    businessNumberShortLabel() {
      const code = this.details?.country || 'AU';
      return getBusinessNumberShortLabel(code);
    },
    formattedAcceptedDate() {
      if (!this.details.accepted_at) return 'N/A';
      return this.details.accepted_at;
    },
    relativeAcceptedTime() {
      if (!this.details.accepted_at) return '';
      if (!moment(this.details.accepted_at).isValid()) return '';
      return moment(this.details.accepted_at).fromNow();
    },
    acceptedOptionText() {
      if (!this.details.option_index_selected) return 'N/A';
      if (this.details.option_index_selected === 1) return 'Main Option';
      return `Option ${toOptionName(this.details.option_index_selected)}`;
    },
    signerName() {
      if (this.details.signers && this.details.signers.length > 0) {
        const matching = this.details.signers.find(s => 
          this.details.accepted_by_email && s.email && s.email.toLowerCase() === this.details.accepted_by_email.toLowerCase()
        ) || this.details.signers[0];
        if (matching) {
          const first = matching.first_name || matching.firstName || '';
          const last = matching.last_name || matching.lastName || '';
          const combined = [first, last].filter(Boolean).join(' ');
          if (combined) return combined;
          if (matching.name) return matching.name;
        }
      }
      if (this.details.primary_contact_first_name || this.details.primary_contact_last_name) {
        const combined = [this.details.primary_contact_first_name, this.details.primary_contact_last_name].filter(Boolean).join(' ');
        if (combined) return combined;
      }
      if (this.details.primary_contact_name) {
        return this.details.primary_contact_name;
      }
      if (!this.details.accepted_by_email) return 'Signer';
      const parts = this.details.accepted_by_email.split('@')[0];
      return parts.replace(/[._+]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    formattedIp() {
      const ip = this.details.accepted_by_ip || '';
      if (!ip) return 'N/A';
      if (ip.includes('[::1]') || ip === '::1' || ip.startsWith('127.0.0.1')) {
        return 'Localhost (::1)';
      }
      // Remove port if present (e.g. 192.168.1.1:52596 -> 192.168.1.1)
      return ip.split(':')[0] || ip;
    }
  }
};
</script>

<style scoped>
.spark-meta-former-label {
  font-size: 0.72rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.spark-accepted-items-list-modern {
  display: flex;
  flex-direction: column;
}

.spark-accepted-item-row-modern {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s ease;
}

.spark-accepted-item-row-modern:last-child {
  border-bottom: none;
}

.spark-accepted-item-row-modern:hover {
  background: #f8fafc;
}

.spark-accepted-item-row-modern__info {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 70%;
}

.spark-accepted-item-row-modern__name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

.spark-accepted-item-row-modern__qty {
  font-size: 0.7rem;
  font-weight: 600;
}

.spark-accepted-item-row-modern__price {
  font-size: 0.875rem;
  font-weight: 700;
  color: #059669;
  white-space: nowrap;
}
</style>
